// Core

// Libraries
import { requestVantageApi } from './request';

/**
 * Fetches the list of available entitlement modules from the mgnt API.
 * Returns null on failure or invalid response.
 *
 * @returns {Promise<Array<string>|null>}
 */
export const fetchEntitlementModules = () => (
  requestVantageApi.customer('get', '/entitlement-modules', null, false)
    .then((response) => (
      Array.isArray(response?.data?.modules) ? response.data.modules : null
    ))
    .catch(() => null)
);

/**
 * Returns whether an optional customer module is entitled.
 * Missing customer, entitlements or module key is treated as not entitled.
 *
 * @param {object|null|undefined} customer
 * @param {string} moduleKey
 * @returns {boolean}
 */
export const isModuleEntitled = (customer, moduleKey) => {
  if (!customer || !customer.entitlements) {
    return false;
  }

  return customer.entitlements[moduleKey] === true;
};

/**
 * Compares two entitlements maps key-by-key. Avoids Redux updates when unchanged.
 *
 * @param {object|null|undefined} left
 * @param {object|null|undefined} right
 * @returns {boolean}
 */
export const areEntitlementsEqual = (left, right) => {
  const leftEntitlements = left && typeof left === 'object' ? left : {};
  const rightEntitlements = right && typeof right === 'object' ? right : {};
  const keys = new Set([
    ...Object.keys(leftEntitlements),
    ...Object.keys(rightEntitlements),
  ]);

  return [...keys].every(
    (moduleKey) => leftEntitlements[moduleKey] === rightEntitlements[moduleKey],
  );
};

/**
 * Fetches the current entitlements state for a customer, for the Admin Panel drawer.
 *
 * @param {string} idCustomer
 * @returns {Promise<object>}
 */
export const fetchAdminCustomerEntitlements = (idCustomer) => (
  requestVantageApi.customer('get', `/${idCustomer}/entitlements`, null, false)
    .then((response) => {
      // Never fall back to a blank payload: the caller would offer an all-disabled form
      // and a subsequent save would silently turn off modules that are enabled.
      if (!response?.data || typeof response.data !== 'object') {
        throw new Error('Invalid entitlements payload');
      }

      return response.data;
    })
);

/**
 * Persists an entitlements update for a customer, from the Admin Panel drawer.
 *
 * @param {string} idCustomer
 * @param {object} entitlements
 * @returns {Promise<object>}
 */
export const updateAdminCustomerEntitlements = (idCustomer, entitlements) => (
  requestVantageApi.customer(
    'put',
    `/${idCustomer}/entitlements`,
    { entitlements },
    false,
  ).then((response) => (
    response?.data && typeof response.data === 'object'
      ? response.data
      : entitlements
  ))
);

/**
 * Fetches the live entitlements state directly from the customer's own API.
 *
 * @param {object|null|undefined} customer
 * @returns {Promise<object>}
 */
export const fetchCustomerRuntimeEntitlements = (customer) => {
  if (!customer?.api?.user) {
    return Promise.resolve({});
  }

  return requestVantageApi.child(customer.api.user, 'get', '/entitlements', null, false)
    .then((response) => (
      response?.data && typeof response.data === 'object'
        ? response.data
        : {}
    ))
    .catch(() => ({}));
};

/**
 * Merges runtime entitlements from the customer account into the mgnt customer object.
 *
 * @param {object} customer
 * @returns {Promise<object>}
 */
export const applyRuntimeEntitlementsToCustomer = async (customer) => {
  const entitlements = await fetchCustomerRuntimeEntitlements(customer);

  return {
    ...customer,
    entitlements,
  };
};
